import React from "react";
import { Box, Button, Link } from "@mui/material";

import { MaskedTaxId } from "../../components/ui";
import { monoFontFamily } from "../../theme/tokens";
import type { CSITipaltiStaging } from "../../types/CSITypes";
import { EMPTY_VALUE } from "../../utils/format";
import { formatStagingDate, formatStagingDateTime } from "./stagingData";
import { SectionCaption } from "./TipaltiCompare";

export interface RecordLinkHandlers {
  /** Opens the live CSI Pay payee record (PayeeDetailModal). */
  onOpenPayeeDetail?: (payeeId: string) => void;
  /** Opens the records on this page with the same tax ID. Receives the RAW value. */
  onOpenTaxIdMatches?: (taxId: string) => void;
  /** Opens the matched payee's analysis record (PayeeModal). */
  onOpenMatchedPayee?: (payeeId: string) => void;
}

/** An id that opens something when a handler is given, otherwise plain monospace text. */
export function IdLink({
  id,
  onOpen,
  label,
}: {
  id: string;
  onOpen?: (id: string) => void;
  label: string;
}) {
  if (!onOpen) {
    return (
      <Box component="span" sx={{ fontFamily: monoFontFamily }}>
        {id}
      </Box>
    );
  }
  return (
    <Link
      component="button"
      type="button"
      onClick={() => onOpen(id)}
      aria-label={label}
      sx={{
        fontFamily: monoFontFamily,
        fontSize: "inherit",
        verticalAlign: "baseline",
        textAlign: "left",
        overflowWrap: "anywhere",
      }}
    >
      {id}
    </Link>
  );
}

/**
 * The tax ID, masked at render (Show only when `canReveal`). Clicking the masked value,
 * or "Matches on this page", opens the records with the same tax ID; the RAW row value is
 * what gets passed on, never the masked text.
 */
export function TaxIdValue({
  value,
  canReveal,
  onOpenMatches,
}: {
  value: string;
  canReveal: boolean;
  onOpenMatches?: (taxId: string) => void;
}) {
  if (!onOpenMatches || !value) {
    return <MaskedTaxId value={value} canReveal={canReveal} />;
  }
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: 3,
        rowGap: 0.5,
      }}
    >
      <Box
        component="span"
        title="Show records with the same tax ID on this page"
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          // The Show / Hide button inside only toggles the mask.
          if ((event.target as HTMLElement).closest("button")) return;
          onOpenMatches(value);
        }}
        sx={{ cursor: "pointer" }}
      >
        <MaskedTaxId value={value} canReveal={canReveal} />
      </Box>
      <Link
        component="button"
        type="button"
        onClick={() => onOpenMatches(value)}
        sx={{ fontSize: "0.78125rem" }}
      >
        Matches on this page
      </Link>
    </Box>
  );
}

export function IdList({
  ids,
  onOpen,
  label,
}: {
  ids: string[];
  onOpen?: (id: string) => void;
  label: (id: string) => string;
}) {
  if (ids.length === 0) return <>None</>;
  return (
    <Box
      component="span"
      sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
    >
      {ids.map((id, index) => (
        <span key={`${id}-${index}`}>
          <IdLink id={id} onOpen={onOpen} label={label(id)} />
        </span>
      ))}
    </Box>
  );
}

export function DetailItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box
        component="dt"
        sx={{ fontSize: "0.6875rem", color: "text.secondary", mb: 0.5 }}
      >
        {label}
      </Box>
      <Box
        component="dd"
        sx={{
          m: 0,
          fontSize: "0.8125rem",
          color: "text.primary",
          overflowWrap: "anywhere",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export interface RecordDetailsProps extends RecordLinkHandlers {
  row: CSITipaltiStaging;
  /** Admin only: whether the tax ID's Show button appears. */
  canRevealTaxId: boolean;
  /** Opens the "Payee IDs" dialog (the former "Show Payee Details"). */
  onShowPayeeIds?: () => void;
}

const orDash = (value: string | null | undefined) =>
  value && value.trim() ? value : EMPTY_VALUE;

/**
 * "Record details" strip: every identifier and analysis field of the row, with the
 * same links as before (payee record, same tax ID, matched payee, group payees).
 */
export function RecordDetails({
  row,
  canRevealTaxId,
  onOpenPayeeDetail,
  onOpenTaxIdMatches,
  onOpenMatchedPayee,
  onShowPayeeIds,
}: RecordDetailsProps) {
  return (
    <Box component="section" aria-label="Record details">
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <SectionCaption>Record details</SectionCaption>
        {onShowPayeeIds && (
          <Button size="small" onClick={onShowPayeeIds} sx={{ mt: -1 }}>
            Payee IDs
          </Button>
        )}
      </Box>
      <Box
        component="dl"
        sx={{
          m: 0,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          columnGap: 4,
          rowGap: 3,
        }}
      >
        <DetailItem label="Payee ID">
          {row.payeeId ? (
            <IdLink
              id={row.payeeId}
              onOpen={onOpenPayeeDetail}
              label={`Open CSI Pay payee ${row.payeeId}`}
            />
          ) : (
            EMPTY_VALUE
          )}
        </DetailItem>
        <DetailItem label="Tax ID">
          <TaxIdValue
            value={row.tin}
            canReveal={canRevealTaxId}
            onOpenMatches={onOpenTaxIdMatches}
          />
        </DetailItem>
        <DetailItem label="Matched payee ID">
          {row.matchedPayeeId ? (
            <IdLink
              id={row.matchedPayeeId}
              onOpen={onOpenMatchedPayee}
              label={`Open matched payee ${row.matchedPayeeId}`}
            />
          ) : (
            "None"
          )}
        </DetailItem>
        <DetailItem label="Group payee IDs">
          <IdList
            ids={row.groupPayeeIds}
            onOpen={onOpenPayeeDetail}
            label={(id) => `Open CSI Pay payee ${id}`}
          />
        </DetailItem>
        <DetailItem label="Additional matched IDs">
          <IdList
            ids={row.additionalMatchedPayeeIds}
            label={(id) => `Payee ${id}`}
          />
        </DetailItem>
        <DetailItem label="Match basis">{orDash(row.matchBasis)}</DetailItem>
        <DetailItem label="CSI created">
          {formatStagingDate(row.csiCreatedDate)}
        </DetailItem>
        <DetailItem label="Analysis created">
          {formatStagingDate(row.analysisCreatedOn)}
        </DetailItem>
        <DetailItem label="Analysis completed">
          {formatStagingDate(row.analysisCompletedOn)}
        </DetailItem>
        <DetailItem label="Analysis completed by">
          {orDash(row.analysisCompletedBy)}
        </DetailItem>
        <DetailItem label="Payable reason">
          {orDash(row.payableReason)}
        </DetailItem>
        <DetailItem label="Last modified">
          {formatStagingDateTime(row.lastModified)}
        </DetailItem>
        <DetailItem label="Last modified by">
          {orDash(row.lastModifiedBy)}
        </DetailItem>
      </Box>
    </Box>
  );
}

export default RecordDetails;
